/**
 * Dedicated axios instance for customer-facing API calls.
 *
 * Completely separate from the admin client (api/client.js):
 * - Reads `customer_token` from localStorage (NOT `token`)
 * - On 401 → cleans up token silently (NO redirect)
 * - Redirect to /account/login is CustomerProtectedRoute's job
 * - NO billing event dispatching (customers have no billing)
 * - NO admin side-effects
 */

import axios from 'axios';

const customerApi = axios.create({
  headers: { 'Content-Type': 'application/json' },
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

customerApi.interceptors.request.use((config) => {
  if (config.url && !config.url.startsWith('http')) {
    const path = config.url.startsWith('/api')
      ? config.url
      : `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`;
    config.url = `${BACKEND_URL}${path}`;
  }
  delete config.baseURL;

  const token = localStorage.getItem('customer_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401: clean up stale token silently — never redirect.
// Public routes (storefront) must degrade to guest, not break.
// Protected routes use CustomerProtectedRoute for redirect logic.
customerApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('customer_token');
    }
    return Promise.reject(error);
  },
);

export default customerApi;
